import React from 'react';
import { Label } from '../ui/label';
import { Check, Star } from 'lucide-react';
import { SHUTTER_STYLES, MATERIALS } from './constants';

const StepConfigure = ({ formData, updateFormData }) => {
  const selectedMaterial = MATERIALS.find(m => m.id === formData.material);
  
  return (
  <div className="space-y-8">
    <div className="text-center mb-6">
      <h2 className="text-xl font-semibold text-gray-900">Configure Your Shutters</h2>
      <p className="text-sm text-gray-500 mt-1">Choose your perfect style</p>
    </div>
    
    {/* Shutter Style */}
    <div className="space-y-4">
      <Label className="text-base font-medium">Shutter Style</Label>
      <div className="grid grid-cols-3 gap-3">
        {SHUTTER_STYLES.map(style => (
          <div
            key={style.id}
            onClick={() => updateFormData('shutterStyle', style.id)}
            className={`cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
              formData.shutterStyle === style.id 
                ? 'border-[#8CB89F] ring-2 ring-[#8CB89F]/20' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`style-${style.id}`}
          >
            <div className="aspect-video bg-gray-100 relative">
              <img 
                src={style.image} 
                alt={style.name}
                className="w-full h-full object-cover"
              />
              {formData.shutterStyle === style.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#8CB89F] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div className="p-2 text-center">
              <p className="font-medium text-sm">{style.name}</p>
              <p className="text-xs text-gray-500">{style.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Material */}
    <div className="space-y-4">
      <Label className="text-base font-medium">Material</Label>
      <div className="space-y-2">
        {MATERIALS.map(material => (
          <div
            key={material.id}
            onClick={() => updateFormData('material', material.id)}
            className={`cursor-pointer rounded-lg border-2 p-4 transition-all relative ${
              formData.material === material.id 
                ? 'border-[#8CB89F] bg-[#8CB89F]/5' 
                : material.recommended 
                  ? 'border-amber-400 hover:border-amber-500 bg-amber-50/50'
                  : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`material-${material.id}`}
          >
            {/* Recommended Badge */}
            {material.recommended && (
              <div className="absolute -top-3 left-4 px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1 shadow-sm">
                <Star className="w-3 h-3 fill-white" />
                {material.recommendedLabel}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {formData.material === material.id && (
                  <div className="w-5 h-5 bg-[#8CB89F] rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{material.name}</p>
                  <p className="text-sm text-gray-500">{material.description}</p>
                  {material.note && (
                    <p className="text-xs text-blue-600 mt-1">{material.note}</p>
                  )}
                </div>
              </div>
              <div className="text-2xl">
                {material.indicator}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Material Features - Show when material with features is selected */}
    {selectedMaterial?.features && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="font-medium text-blue-900 mb-2">{selectedMaterial.name} Features:</p>
        <div className="grid grid-cols-2 gap-1">
          {selectedMaterial.features.map((feature, index) => (
            <p key={index} className="text-sm text-blue-800 flex items-center gap-1">
              <Check className="w-3 h-3 text-blue-600" /> {feature}
            </p>
          ))}
        </div>
      </div>
    )}
    
    {/* Colour Note */}
    <div className="bg-[#8CB89F]/10 border border-[#8CB89F]/30 rounded-lg p-4 text-center">
      <p className="text-[#6a9a7d] font-medium">
        We Can Make Shutters In Any Colour
      </p>
      <p className="text-sm text-gray-600 mt-1">
        Discuss your colour preferences during your free home visit
      </p>
    </div>
  </div>
);
};

export default StepConfigure;
